import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { SectionHeading, CTABand } from "../components/blocks";
import { COMPANY, ZONES_OISE, IMAGES } from "../lib/data";
import { MapPin, Home, Clock } from "lucide-react";

export default function ZoneIntervention() {
  return (
    <div>
      <Seo
        title="Zone d'intervention — Couvreur Oise, Île-de-France, Picardie | Carnier"
        description="Carnier Toiture et Habitat intervient dans tout le département de l'Oise (60), ainsi qu'en Île-de-France et en Picardie pour vos travaux de toiture et de façade."
      />

      <section className="bg-vegetal-800 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">Zone d'intervention</span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-balance">Un artisan local, une couverture large</h1>
            <p className="mt-5 max-w-2xl text-vegetal-100/80 text-lg">Nous intervenons en priorité dans l'Oise, et nous nous déplaçons aussi en Île-de-France et en Picardie.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <SectionHeading overline="Notre priorité : l'Oise" title="Au plus près des maisons de l'Oise" />
              <p className="mt-5 text-neutral-700 leading-relaxed">
                L'Oise, c'est notre territoire. Nous connaissons bien les maisons individuelles de la région, souvent dotées de <strong className="text-neutral-900">grandes parcelles</strong> et de toitures exposées aux intempéries. Cette proximité nous permet d'intervenir rapidement et de vous conseiller au plus juste.
              </p>
              <p className="mt-4 text-neutral-700 leading-relaxed">
                Que vous soyez à Beauvais, Compiègne, Creil, Senlis ou Chantilly, nous nous déplaçons pour évaluer votre projet et vous proposer un devis gratuit.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {ZONES_OISE.map((z) => (
                  <span key={z} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-terracotta-50 text-terracotta-700 text-sm font-medium border border-terracotta-100">
                    <MapPin className="h-3.5 w-3.5" /> {z}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5">
              <img src={IMAGES.roofAfter} alt="Maison individuelle rénovée dans l'Oise" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white/60 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20">
          <Reveal><SectionHeading center overline="Une couverture élargie" title="Oise, Île-de-France & Picardie" /></Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-6">
            {[
              { icon: MapPin, t: "Oise (60)", d: "Notre secteur principal : tout le département, avec une intervention rapide et un vrai ancrage local." },
              { icon: Home, t: "Île-de-France", d: "Nous nous déplaçons pour vos projets de toiture, façade et amélioration de l'habitat." },
              { icon: Clock, t: "Picardie", d: "Une capacité d'intervention élargie pour répondre à vos besoins dans la région." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 80}>
                <div className="rounded-xl bg-white border border-neutral-200 p-7 shadow-sm h-full text-center">
                  <div className="mx-auto grid place-items-center h-14 w-14 rounded-xl bg-vegetal-50 text-vegetal-600">
                    <c.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-neutral-900">{c.t}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-10 text-center text-neutral-600 text-sm">
              Vous n'êtes pas sûr d'être dans notre zone ? Indiquez-nous votre code postal via le formulaire de devis, ou appelez le{" "}
              <a href={`tel:${COMPANY.phoneRaw}`} className="text-terracotta-600 font-medium">{COMPANY.phone}</a>.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
